import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Eye, Loader2, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { WhatsAppIcon } from "../components/Brand";

const TYPES = [["", "All types"], ["home", "Homepage"], ["product", "Product"], ["bulk", "Bulk"], ["franchise", "Franchise"], ["contact", "Contact"]];
export const STATUSES = [["new", "New"], ["hot", "Hot"], ["follow_up", "Follow-up"], ["contacted", "Contacted"], ["converted", "Converted"], ["lost", "Lost"]];
const STATUS_CLS = { new: "bg-orange-100 text-orange-700", hot: "bg-red-100 text-red-700", follow_up: "bg-amber-100 text-amber-800", contacted: "bg-blue-100 text-blue-700", converted: "bg-emerald-100 text-emerald-700", lost: "bg-slate-200 text-slate-600" };
const fmtDate = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");
const waNum = (p) => { const d = (p || "").replace(/\D/g, ""); return d.length === 10 ? `91${d}` : d; };

const LeadDialog = ({ lead, onClose, onSave }) => {
  const [notes, setNotes] = useState("");
  const [fu, setFu] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setNotes(lead?.notes ?? "");
    setFu(lead?.follow_up_date ?? "");
  }, [lead]);
  if (!lead) return null;
  const rows = [["Date", fmtDate(lead.created_at)], ["Form", lead.type], ["Mobile", lead.phone], ["WhatsApp", lead.whatsapp], ["Email", lead.email], ["Company", lead.company], ["Business type", lead.business_type], ["City", lead.city], ["State", lead.state], ["Product", lead.product], ["Quantity", lead.quantity], ...Object.entries(lead.details || {}), ["Message", lead.message], ["Traffic source", lead.source], ["Approx. location", lead.location], ["Page", lead.page]].filter(([, v]) => v);
  const save = async () => {
    setSaving(true);
    await onSave(lead.id, { notes, follow_up_date: fu });
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-lead-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader><DialogTitle>{lead.name}</DialogTitle></DialogHeader>
        <dl className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
          {rows.map(([k, v]) => <div key={k} className="contents"><dt className="text-slate-500">{k}</dt><dd className="col-span-2 whitespace-pre-wrap font-medium text-navy">{v}</dd></div>)}
        </dl>
        <a href={`https://wa.me/${waNum(lead.whatsapp || lead.phone)}?text=${encodeURIComponent(`Hello ${lead.name}, thank you for your enquiry with MHM SUPER ADBLUE.`)}`} target="_blank" rel="noreferrer" data-testid="admin-lead-whatsapp" className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white"><WhatsAppIcon className="h-4 w-4" /> Follow up on WhatsApp</a>
        <label className="block text-sm font-semibold text-navy">Follow-up date
          <input type="date" data-testid="admin-lead-followup" value={fu} onChange={(e) => setFu(e.target.value)} className="field mt-1" />
        </label>
        <label className="block text-sm font-semibold text-navy">Notes
          <textarea rows={3} data-testid="admin-lead-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="field mt-1" />
        </label>
        <button data-testid="admin-lead-save" onClick={save} disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-white">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save notes</button>
      </DialogContent>
    </Dialog>
  );
};

export default function LeadsTab({ onChange, refreshKey }) {
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);

  const load = useCallback(() => {
    api.get("/enquiries", { params: type ? { type } : {} }).then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errorText(e)));
  }, [type]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const shown = useMemo(() => {
    const s = q.toLowerCase();
    return rows.filter((r) => (!status || r.status === status) && (!s || [r.name, r.phone, r.whatsapp, r.company, r.city, r.product, r.email].join(" ").toLowerCase().includes(s)));
  }, [rows, status, q]);

  const patch = async (id, body) => {
    try {
      const { data } = await api.patch(`/enquiries/${id}`, body);
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
      if (view?.id === id) setView(data);
      onChange?.();
      toast.success("Lead updated");
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/enquiries/${id}`);
    setRows((rs) => rs.filter((r) => r.id !== id));
    onChange?.();
  };
  const exportXlsx = async () => {
    const res = await api.get("/enquiries/export", { params: { ...(type && { type }), ...(status && { status }), ...(q && { q }) }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mhm-leads-${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-2xl border border-navy/10 bg-white">
      <div className="flex flex-wrap items-center gap-2 border-b border-navy/10 p-4">
        <label className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="admin-lead-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, company, city, product" className="field !py-2 !pl-9 text-sm" />
        </label>
        <select data-testid="admin-lead-type-filter" value={type} onChange={(e) => setType(e.target.value)} className="field !w-auto !py-2 text-sm">{TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select data-testid="admin-lead-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="field !w-auto !py-2 text-sm"><option value="">All statuses</option>{STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <button data-testid="admin-export-xlsx" onClick={exportXlsx} className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand"><Download className="h-4 w-4" /> Excel</button>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="admin-enquiries-table" className="w-full min-w-[980px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="p-3">Date</th><th className="p-3">Lead</th><th className="p-3">Phone</th><th className="p-3">Business</th><th className="p-3">Product / Qty</th><th className="p-3">Location</th><th className="p-3">Source</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} data-testid={`admin-enquiry-row-${r.id}`} className="border-t border-navy/5 align-top">
                <td className="p-3 whitespace-nowrap text-slate-600">{fmtDate(r.created_at)}<div className="text-xs capitalize text-slate-400">{r.type}</div></td>
                <td className="p-3 font-semibold text-navy">{r.name}<div className="text-xs font-normal text-slate-500">{r.company}</div></td>
                <td className="p-3 whitespace-nowrap">{r.phone}{r.whatsapp && r.whatsapp !== r.phone && <div className="text-xs text-slate-500">WA {r.whatsapp}</div>}</td>
                <td className="p-3">{r.business_type}</td>
                <td className="p-3">{r.product || r.details?.Product}<div className="text-xs text-slate-500">{r.quantity}</div></td>
                <td className="p-3">{[r.city, r.state].filter(Boolean).join(", ")}</td>
                <td className="p-3 text-xs">{r.source || "—"}</td>
                <td className="p-3">
                  <select data-testid={`admin-enquiry-status-${r.id}`} value={r.status} onChange={(e) => patch(r.id, { status: e.target.value })} className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_CLS[r.status] || ""}`}>
                    {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {r.follow_up_date && <div className="mt-1 text-xs text-amber-700">Follow-up {r.follow_up_date}</div>}
                </td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`admin-enquiry-view-${r.id}`} onClick={() => setView(r)} className="rounded-lg p-2 hover:bg-slate-100" aria-label="View"><Eye className="h-4 w-4" /></button>
                    <a href={`https://wa.me/${waNum(r.whatsapp || r.phone)}`} target="_blank" rel="noreferrer" className="rounded-lg p-2 text-[#128C4B] hover:bg-slate-100" aria-label="WhatsApp"><WhatsAppIcon className="h-4 w-4" /></a>
                    <button data-testid={`admin-enquiry-delete-${r.id}`} onClick={() => remove(r.id)} className="rounded-lg p-2 text-red-600 hover:bg-red-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-slate-500">No leads found.</td></tr>}
          </tbody>
        </table>
      </div>
      <LeadDialog lead={view} onClose={() => setView(null)} onSave={patch} />
    </div>
  );
}
